import React from 'react';
import withShipment from '../../withShipment';
import CustomToolbar from '../global/CustomToolbar';
import FeedbackBar from '../feedbackBar/FeedbackBar';
import { userInfoSelector } from '../../redux/selectors/auth';
import { weeklyReportAuditLogsSelector } from '../../redux/selectors/audit';
import { listWeeklyReportAudit } from '../../redux/actions/audit';
import {
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import RefreshIcon from '@mui/icons-material/Refresh';
import KeyboardBackspaceIcon from '@material-ui/icons/KeyboardBackspace';

const ACTION_COLOR = {
  REPORT_FIELD_UPDATE: 'info',
  MANAGER_BANK_ADD: 'success',
  MANAGER_SUMMARY_UPDATE: 'warning',
  MANAGER_SETTING_UPDATE: 'secondary',
  TRANSACTION_MODIFIED: 'error',
  CASH_LEFT_MODIFIED: 'error',
};

const FIELD_LABELS = {
  jpOtherIn: 'JP Other In',
  jpOtherOut: 'JP Other Out',
  endingBalance: 'Ending Balance',
  openingBalance: 'Opening Balance',
  morningPull: 'Morning Pull',
  nightPull: 'Night Pull',
  addBank: 'Add Bank',
  loanIn: 'Loan In',
  employeeShortPay: 'Employee Short Pay',
  minusBank: 'Minus Bank',
  jackpotNotPay: 'Jackpot Not Pay',
  bank1: 'Bank 1',
  bank2: 'Bank 2',
  bank3: 'Bank 3',
  manager: 'Manager',
  atm: 'ATM',
  zelle: 'Zelle',
  supervisor: 'Supervisor',
  minusRollover: 'Minus Rollover',
  lastWeekProfit: 'Last Week Profit',
  expenseSet: 'Expenses',
  managerName: 'Manager Name',
  profitThreshold: 'Profit Threshold',
  managerPercentage: 'Manager %',
  amount: 'Amount',
  type: 'Type',
  userName: 'Entered By',
  employeeName: 'Employee',
  createdDate: 'Created',
  delivery: 'Delivery',
  adjustments: 'Adjustments',
  otherItems: 'Other Items',
  atmTransactions: 'ATM Transactions',
  weeklyNetProfit: 'Weekly Net Profit',
  remainingProfit: 'Remaining Profit',
  isWeeklyNetProfitEdited: 'Net Profit Edited',
  totalATMCash: 'Total ATM Cash',
  cashLeft: 'Cash Left',
  description: 'Description',
};

class AuditRow extends React.Component {
  constructor(props) {
    super(props);
    this.state = { open: false };
  }

  isIsoDateString(v) {
    return typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(v);
  }

  toNumber(v) {
    if (v == null || v === '') return 0;
    if (typeof v === 'number') return v;
    if (typeof v === 'string') return Number(v) || 0;
    if (typeof v === 'object' && v.$numberDecimal !== undefined) return Number(v.$numberDecimal) || 0;
    return 0;
  }

  formatMoney(n) {
    const num = this.toNumber(n);
    const abs = Math.abs(num);
    const str = abs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return num < 0 ? `-$${str}` : `$${str}`;
  }

  // Entry point: field name + value. Lets us apply field-specific
  // formatting (percentages, yes/no, dates) before falling back to the
  // generic renderer.
  renderFieldValue(fieldName, v) {
    if (v === null || v === undefined || v === '') return <em>(none)</em>;

    if (fieldName === 'managerPercentage') {
      return `${this.toNumber(v)}%`;
    }
    if (fieldName === 'isWeeklyNetProfitEdited') {
      return v ? 'Yes' : 'No';
    }
    if (fieldName === 'expenseSet' ||
        fieldName === 'otherItems' ||
        fieldName === 'atmTransactions') {
      return this.renderEntryList(Array.isArray(v) ? v : []);
    }
    if (fieldName === 'adjustments') {
      return this.renderAdjustments(v);
    }
    if (fieldName === 'delivery') {
      return this.renderDelivery(v);
    }
    return this.formatValue(v);
  }

  formatValue(v) {
    if (v === null || v === undefined || v === '') return <em>(none)</em>;
    if (typeof v === 'boolean') return v ? 'Yes' : 'No';
    if (v instanceof Date) return this.formatDate(v);
    if (this.isIsoDateString(v)) return this.formatDate(v);
    if (typeof v === 'number') return this.formatMoney(v);
    if (typeof v === 'string') {
      const trimmed = v.trim();
      if (/^-?\d+(\.\d+)?$/.test(trimmed)) return this.formatMoney(Number(trimmed));
      return v;
    }
    if (typeof v !== 'object') return String(v);

    if (v.$numberDecimal !== undefined) return this.formatMoney(Number(v.$numberDecimal));
    if (v.$date !== undefined) return this.formatDate(v.$date);

    if (Array.isArray(v)) return this.renderEntryList(v);

    return this.renderObjectKeyValue(v);
  }

  renderEntryList(arr) {
    if (!arr || arr.length === 0) return <em>(empty list)</em>;
    return (
      <ul style={{ margin: 0, paddingLeft: 18 }}>
        {arr.map((item, i) => (
          <li key={i} style={{ marginBottom: 4 }}>{this.renderEntryInline(item)}</li>
        ))}
      </ul>
    );
  }

  // Render a single object entry on one line:
  //   **Label** — $Amount (badges)
  //   sub · lines · here
  renderEntryInline(item) {
    if (item == null) return <em>(none)</em>;
    if (typeof item !== 'object') return this.formatValue(item);
    if (item.$numberDecimal !== undefined) return this.formatMoney(Number(item.$numberDecimal));

    const label = item.description || item.type || item.name || '(item)';
    const hasAmount = item.amount !== undefined && item.amount !== null && item.amount !== '';
    const amountText = hasAmount ? this.formatMoney(item.amount) : null;

    const badges = [];
    if (item.isManager) badges.push('Manager');
    if (item.isPermanent) badges.push('Permanent');
    if (item.expenseType && item.expenseType !== label) badges.push(item.expenseType);
    // Manager bank entry: type field is 'add' or 'minus' — show as badge,
    // but only if it's not already being used as the label.
    if (item.type && item.type !== label && (item.type === 'add' || item.type === 'minus')) {
      badges.push(item.type === 'add' ? 'Added' : 'Deducted');
    }

    const subParts = [];
    if (item.comments) subParts.push(item.comments);
    if (item.assignedTo) subParts.push(`Assigned: ${item.assignedTo}`);
    if (item.employeeName) subParts.push(`Employee: ${item.employeeName}`);
    if (item.userName) subParts.push(`By: ${item.userName}`);
    if (item.createdDate) subParts.push(this.formatDate(item.createdDate));

    return (
      <span>
        <strong>{label}</strong>
        {amountText != null && (
          <span style={{ fontVariantNumeric: 'tabular-nums' }}> — {amountText}</span>
        )}
        {badges.length > 0 && (
          <span style={{ color: '#666', fontSize: '0.9em' }}>
            {' '}({badges.join(', ')})
          </span>
        )}
        {subParts.length > 0 && (
          <div style={{ fontSize: '0.85em', color: '#666', marginLeft: 4 }}>
            {subParts.join(' · ')}
          </div>
        )}
      </span>
    );
  }

  renderObjectKeyValue(obj) {
    const keys = Object.keys(obj).filter(
      (k) => obj[k] !== undefined && obj[k] !== null && obj[k] !== ''
    );
    if (keys.length === 0) return <em>(empty)</em>;
    return (
      <div>
        {keys.map((k) => (
          <div key={k} style={{ display: 'flex', gap: 6, fontSize: '0.92em' }}>
            <span style={{ color: '#555', minWidth: 110 }}>
              {this.formatFieldName(k)}:
            </span>
            <span>{this.formatValue(obj[k])}</span>
          </div>
        ))}
      </div>
    );
  }

  renderAdjustments(v) {
    if (!v || typeof v !== 'object') return <em>(none)</em>;
    const additions = Array.isArray(v.additions) ? v.additions : [];
    const deductions = Array.isArray(v.deductions) ? v.deductions : [];
    return (
      <div>
        <div style={{ marginBottom: 6 }}>
          <div style={{ fontWeight: 600, fontSize: '0.9em' }}>Additions</div>
          {this.renderEntryList(additions)}
        </div>
        <div style={{ marginBottom: 6 }}>
          <div style={{ fontWeight: 600, fontSize: '0.9em' }}>Deductions</div>
          {this.renderEntryList(deductions)}
        </div>
        {v.adjustedTotal !== undefined && v.adjustedTotal !== null && (
          <div style={{ fontSize: '0.9em' }}>
            <strong>Adjusted Total:</strong>{' '}
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>
              {this.formatMoney(v.adjustedTotal)}
            </span>
          </div>
        )}
      </div>
    );
  }

  renderDelivery(v) {
    if (!v || typeof v !== 'object') return <em>(none)</em>;
    const rows = [
      ['Cash', v.cash],
      ['Manager Zelle', v.managerZelle],
      ['ATM Receipts', v.atmReceipts],
      ['ATM Advanced', v.atmAdv],
      ['Total Delivered', v.totalDelivered],
    ];
    return (
      <div>
        {rows.map(([label, val]) => (
          <div key={label} style={{ display: 'flex', gap: 6, fontSize: '0.92em' }}>
            <span style={{ color: '#555', minWidth: 130 }}>{label}:</span>
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>
              {this.formatMoney(val)}
            </span>
          </div>
        ))}
      </div>
    );
  }

  formatDate(d) {
    if (!d) return '(none)';
    try {
      const date = new Date(d);
      const datePart = date.toLocaleDateString('en-US', {
        timeZone: 'America/Chicago',
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
      const timePart = date.toLocaleTimeString('en-US', {
        timeZone: 'America/Chicago',
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
        timeZoneName: 'short',
      });
      return `${datePart} · ${timePart}`;
    } catch (e) {
      return String(d);
    }
  }

  formatFieldName(f) {
    return FIELD_LABELS[f] || f;
  }

  render() {
    const { row, onViewShiftReport } = this.props;
    const { open } = this.state;
    const changedFields = row.changedFields || [];
    const canViewShiftReport = !!(
      row.targetClockId &&
      row.targetUserId &&
      row.locationId &&
      onViewShiftReport
    );
    return (
      <React.Fragment>
        <TableRow hover>
          <TableCell style={{ width: 40 }}>
            <IconButton size="small" onClick={() => this.setState({ open: !open })}>
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          </TableCell>
          <TableCell>{this.formatDate(row.changedAt)}</TableCell>
          <TableCell>{row.userName || '—'}</TableCell>
          <TableCell>
            <Chip
              size="small"
              label={row.action}
              color={ACTION_COLOR[row.action] || 'default'}
            />
          </TableCell>
          <TableCell>{row.readingSessionId || '—'}</TableCell>
          <TableCell>{row.targetUserName || '—'}</TableCell>
          <TableCell>
            {changedFields.map((f) => this.formatFieldName(f)).join(', ') || '(none)'}
          </TableCell>
          <TableCell style={{ width: 170 }}>
            {canViewShiftReport ? (
              <Button
                size="small"
                variant="outlined"
                startIcon={<DescriptionIcon />}
                onClick={() =>
                  onViewShiftReport({
                    userId: row.targetUserId,
                    locationId: row.locationId,
                    clockId: row.targetClockId,
                  })
                }
              >
                View Shift Report
              </Button>
            ) : null}
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ margin: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Changes
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Field</strong></TableCell>
                      <TableCell><strong>Old Value</strong></TableCell>
                      <TableCell><strong>New Value</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {changedFields.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={3}>
                          <em>No field-level diff recorded.</em>
                        </TableCell>
                      </TableRow>
                    )}
                    {changedFields.map((f) => (
                      <TableRow key={f}>
                        <TableCell style={{ verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                          {this.formatFieldName(f)}
                        </TableCell>
                        <TableCell style={{ color: '#b71c1c', verticalAlign: 'top' }}>
                          {this.renderFieldValue(f, row.oldValues && row.oldValues[f])}
                        </TableCell>
                        <TableCell style={{ color: '#1b5e20', verticalAlign: 'top' }}>
                          {this.renderFieldValue(f, row.newValues && row.newValues[f])}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      </React.Fragment>
    );
  }
}

class WeeklyReportAuditLog extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      feedbackBarOpen: false,
      feedbackMessage: '',
      feedbackStatus: '',
    };
  }

  componentDidMount() {
    this.fetch();
  }

  fetch = () => {
    const payload = {
      locationId: this.props.userInfo.selectedLocationId,
    };
    if (Array.isArray(this.props.sessionIds) && this.props.sessionIds.length) {
      payload.readingSessionIds = this.props.sessionIds;
    } else if (this.props.embeddedSessionId) {
      payload.readingSessionId = this.props.embeddedSessionId;
    }
    this.props.listWeeklyReportAudit(payload);
  };

  closeFeedbackBar = () => {
    this.setState({ feedbackBarOpen: false });
  };

  render() {
    const logs = this.props.logs || [];
    const isEmbedded = !!this.props.onBack;
    const titleSuffix = this.props.embeddedSessionId
      ? ` — Session #${this.props.embeddedSessionId}`
      : '';

    const headerButtons = [];
    if (isEmbedded) {
      headerButtons.push({
        icon: <KeyboardBackspaceIcon />,
        handler: this.props.onBack,
        title: 'Back',
      });
    }
    headerButtons.push({
      icon: <RefreshIcon />,
      handler: this.fetch,
      title: 'Refresh',
    });

    return (
      <React.Fragment>
        <CustomToolbar
          title={`Weekly Report Audit Log${titleSuffix}`}
          createTitle={''}
          backTitle={''}
          headerButtons={headerButtons}
        />
        <FeedbackBar
          open={this.state.feedbackBarOpen}
          handleClose={this.closeFeedbackBar}
          severity={this.state.feedbackStatus}
          message={this.state.feedbackMessage}
        />

        <TableContainer component={Paper} sx={{ margin: 2, width: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell><strong>When</strong></TableCell>
                <TableCell><strong>Who</strong></TableCell>
                <TableCell><strong>Action</strong></TableCell>
                <TableCell><strong>Session #</strong></TableCell>
                <TableCell><strong>Employee</strong></TableCell>
                <TableCell><strong>Changed Fields</strong></TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <em>No audit entries.</em>
                  </TableCell>
                </TableRow>
              )}
              {logs.map((row) => (
                <AuditRow
                  key={row.auditId}
                  row={row}
                  onViewShiftReport={this.props.onViewShiftReport}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state) => ({
  userInfo: userInfoSelector(state),
  logs: weeklyReportAuditLogsSelector(state),
});

const actionCreators = {
  listWeeklyReportAudit,
};

export default withShipment({ mapStateToProps, actionCreators }, WeeklyReportAuditLog);
